<template>
    <div class="user-tab-panel">
        <v-row>
            <v-col cols="12">
                <v-select
                    v-model="payload.role"
                    label="Tipo de usuário"
                    outlined
                    hide-details="auto"
                    item-text="text"
                    item-value="value"
                    disabled
                    :items="$user.roles"
                />
            </v-col>

            <v-col cols="12">
                <v-text-field
                    autocomplete="nope"
                    v-model="payload.name"
                    label="Nome"
                    outlined
                    hide-details="auto"
                />
            </v-col>

            <v-col cols="12">
                <v-text-field
                    autocomplete="nope"
                    v-model="payload.email"
                    label="E-mail"
                    outlined
                    hide-details="auto"
                />
            </v-col>

            <v-col
                cols="12"
                v-if="['user', 'partner'].includes(payload.role) && $store.state.currentUser?.role === 'admin'"
            >
                <v-autocomplete
                    multiple
                    v-model="otherPermissions"
                    label="Permissões"
                    outlined
                    hide-details="auto"
                    item-text="name"
                    item-value="id"
                    :items="availablePermissions"
                />
            </v-col>

            <template v-if="payload.role === 'user'">
                <v-col v-if="$store.state.currentUser?.role === 'admin'" cols="12">
                    <v-text-field
                        autocomplete="nope"
                        type="number"
                        min="0"
                        step="1"
                        v-model.number="payload.articles_quantity"
                        label="Saldo disponível de artigos"
                        outlined
                        hide-details="auto"
                        hint="Ajuste pontual do saldo; não altera a quantidade mensal."
                        persistent-hint
                    />
                </v-col>

                <v-col
                    cols="12"
                    v-if="payload.permissions?.includes('SOCIAL_NETWORK_ASSISTANT')"
                >
                    <v-text-field
                        autocomplete="nope"
                        type="number"
                        v-model="payload.social_post_quantity"
                        label="Saldo de postagens sociais"
                        outlined
                        hide-details="auto"
                    />
                </v-col>

                <template v-if="$store.state.currentUser?.role === 'admin'">
                    <v-col cols="12">
                        <v-divider class="mb-4" />
                        <div class="text-subtitle-1 font-weight-bold">Acessos da assinatura</div>
                        <p class="text-body-2 text--secondary mb-0">
                            Siga os benefícios do plano ou defina uma exceção para este usuário.
                        </p>
                    </v-col>
                    <v-col cols="12" sm="6">
                        <v-select v-model="rampUpOverride" :items="overrideOptions"
                            label="Ramp-up" outlined hide-details="auto" />
                    </v-col>
                    <v-col cols="12" sm="6">
                        <v-select v-model="articlesOverride" :items="overrideOptions"
                            label="Artigos" outlined hide-details="auto" />
                    </v-col>
                    <v-col cols="12">
                        <v-select v-model="conversationAnalysisOverride" :items="overrideOptions"
                            label="Análise de atendimentos" outlined hide-details="auto" persistent-hint
                            hint="Sem este acesso, análises manuais e automáticas ficam bloqueadas. Captura e transcrição do WhatsApp continuam." />
                    </v-col>
                    <v-col cols="12">
                        <v-select v-model="geoSeoOverride" :items="overrideOptions"
                            label="Presença GEO e SEO" outlined hide-details="auto" persistent-hint
                            :hint="`Prompts ativos permitidos: ${payload.geo_seo_prompt_limit || 1}.`" />
                    </v-col>
                    <v-col v-if="payload.subscription_overrides.geo_seo === true" cols="12">
                        <v-text-field v-model.number="payload.subscription_overrides.geo_seo_prompt_limit"
                            type="number" min="1" step="1" label="Limite de prompts ativos (override)" outlined hide-details="auto" persistent-hint
                            hint="Ao reduzir o limite, os excedentes serão pausados. Os prompts mais antigos permanecem ativos." />
                    </v-col>
                    <v-col v-if="payload.subscription_overrides.articles === true" cols="12">
                        <v-text-field v-model.number="payload.subscription_overrides.articles_quantity"
                            type="number" min="1" max="9999" step="1" label="Quantidade mensal de artigos (override)"
                            outlined hide-details="auto" persistent-hint
                            hint="Renova todo dia 1, sem acumular. Mudanças na quantidade valem no próximo mês; a primeira liberação é imediata." />
                    </v-col>
                    <v-col v-if="payload.subscription_overrides.ramp_up !== null" cols="12">
                        <v-alert dense text type="info" class="mb-0">
                            Com uma exceção de Ramp-up, a publicação e a despublicação do site ficam sob responsabilidade do administrador.
                        </v-alert>
                    </v-col>
                </template>

                <v-col cols="12">
                    <r-server-autocomplete
                        v-model="payload.vindi_customer_id"
                        autocomplete="nope"
                        label="Cliente Vindi"
                        outlined
                        hide-details="auto"
                        request="searchVindiCustomers"
                        initial-value-request="getVindiCustomerById"
                        item-text="email"
                        item-value="id"
                        clearable
                    >
                        <template #selection="{ item, attrs }">
                            <v-list-item-content v-bind="attrs">
                                <v-list-item-title>{{ item.name }}</v-list-item-title>
                                <v-list-item-subtitle>ID: {{ item.id }} - Email: {{ item.email }}</v-list-item-subtitle>
                            </v-list-item-content>
                        </template>
                        <template #item="{ item, attrs }">
                            <v-list-item-content v-bind="attrs">
                                <v-list-item-title>{{ item.name }}</v-list-item-title>
                                <v-list-item-subtitle>ID: {{ item.id }} - Email: {{ item.email }}</v-list-item-subtitle>
                            </v-list-item-content>
                        </template>
                    </r-server-autocomplete>
                </v-col>

                <v-col cols="12">
                    <v-text-field
                        autocomplete="nope"
                        v-model="payload.vindi_subscription_id"
                        label="ID Assinatura Vindi"
                        readonly
                        outlined
                        hide-details="auto"
                    />
                </v-col>
                <v-col cols="12">
                    <v-autocomplete
                        v-model="payload.vindi_subscription_id"
                        :items="subscriptions"
                        label="Atribuir Assinatura Vindi"
                        outlined
                        hide-details="auto"
                        item-text="customer.email"
                        item-value="id"
                    >
                        <template #selection="{ item, attrs }">
                            <v-list-item-content v-bind="attrs">
                                <v-list-item-title>{{ item.customer.email }}</v-list-item-title>
                                <v-list-item-subtitle>ID: {{ item.id }} - Plano: {{ item.plan.name }}</v-list-item-subtitle>
                            </v-list-item-content>
                        </template>
                        <template #item="{ item, attrs }">
                            <v-list-item-content v-bind="attrs">
                                <v-list-item-title>{{ item.customer.email }}</v-list-item-title>
                                <v-list-item-subtitle>ID: {{ item.id }} - Plano: {{ item.plan.name }}</v-list-item-subtitle>
                            </v-list-item-content>
                        </template>
                    </v-autocomplete>
                </v-col>
            </template>

            <v-col cols="12">
                <v-text-field
                    autocomplete="nope"
                    v-model="payload.duda_site_name"
                    label="ID do site Duda"
                    outlined
                    hide-details="auto"
                />
            </v-col>

            <v-col
                cols="12"
                v-if="payload.role === 'partner'"
            >
                <v-autocomplete
                    autocomplete="nope"
                    v-model="payload.accesses"
                    label="Acessos"
                    :items="users.filter(user => user.role === 'user')"
                    item-text="name"
                    item-value="_id"
                    multiple
                    outlined
                    hide-details="auto"
                >
                    <template #item="{ item, attrs }">
                        <v-list-item-content v-bind="attrs">
                            <v-list-item-title>{{ item.name }}</v-list-item-title>
                            <v-list-item-subtitle>{{ item.email }}</v-list-item-subtitle>
                        </v-list-item-content>
                    </template>
                    <template #selection="{ item, index }">
                        <div v-if="index === 0">
                            <span>{{ item.name }}</span>
                        </div>
                        <span
                            v-if="index === 1"
                            class="grey--text text-caption"
                        >
                            (+{{ payload.accesses.length - 1 }} outros)
                        </span>
                    </template>
                </v-autocomplete>
            </v-col>
        </v-row>

        <div class="user-tab-panel__actions">
            <v-btn
                color="primary"
                @click="save"
                :loading="saving"
            >
                Salvar
            </v-btn>
        </div>
    </div>
</template>

<script>
export default {
    props: {
        value: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            saving: false,
            payload: { subscription_overrides: { ramp_up: null, articles: null, conversation_analysis: null,
                geo_seo: null, geo_seo_prompt_limit: 1, articles_quantity: null } },
            originalPayload: {},
            overrideOptions: [
                { text: 'Seguir plano', value: 'inherit' },
                { text: 'Liberar', value: 'allow' },
                { text: 'Bloquear', value: 'deny' },
            ],
        }
    },
    computed: {
        rampUpOverride: {
            get() { return this.overrideChoice('ramp_up') },
            set(value) { this.$set(this.payload.subscription_overrides, 'ramp_up', this.overrideValue(value)) },
        },
        articlesOverride: {
            get() { return this.overrideChoice('articles') },
            set(value) { this.$set(this.payload.subscription_overrides, 'articles', this.overrideValue(value)) },
        },
        conversationAnalysisOverride: {
            get() { return this.overrideChoice('conversation_analysis') },
            set(value) { this.$set(this.payload.subscription_overrides, 'conversation_analysis', this.overrideValue(value)) },
        },
        geoSeoOverride: {
            get() { return this.overrideChoice('geo_seo') },
            set(value) { this.$set(this.payload.subscription_overrides, 'geo_seo', this.overrideValue(value)) },
        },
        otherPermissions: {
            get() {
                return (this.payload.permissions || []).filter(permission => this.availablePermissions.some(option => option.id === permission))
            },
            set(value) {
                const hidden = (this.payload.permissions || []).filter(permission => !this.availablePermissions.some(option => option.id === permission))
                this.$set(this.payload, 'permissions', [...hidden, ...value])
            },
        },
        availablePermissions() {
            return this.$user.permissions.filter(permission => this.payload.role !== 'user'
                || !['DEFAULT', 'ARTICLE_ASSISTANT', 'CONVERSATION_ANALYSIS', 'GEO_SEO_PRESENCE'].includes(permission.id))
        },
        user: {
            get() {
                return this.value
            },
            set(value) {
                this.$emit('input', value)
            }
        },
        users() {
            return this.$store.state.users.data
        },
        subscriptions() {
            return this.$store.state.vindiSubscriptions.data
        },

    },
    methods: {
        overrideChoice(feature) {
            const value = this.payload.subscription_overrides[feature]
            return value === true ? 'allow' : value === false ? 'deny' : 'inherit'
        },
        overrideValue(choice) {
            return choice === 'allow' ? true : choice === 'deny' ? false : null
        },
        async load() {
            const payload = JSON.parse(JSON.stringify(this.value || {}))

            payload.accesses = payload.accesses?.map(access => access.user_id) || []
            payload.geo_seo_prompt_limit = payload.geo_seo_prompt_limit || 1

            payload.subscription_overrides = { ramp_up: null, articles: null, conversation_analysis: null,
                geo_seo: null, geo_seo_prompt_limit: null, articles_quantity: null, ...payload.subscription_overrides }
            payload.subscription_overrides.geo_seo_prompt_limit = payload.subscription_overrides.geo_seo_prompt_limit ?? payload.geo_seo_prompt_limit
            this.payload = payload
            this.originalPayload = JSON.parse(JSON.stringify(payload))

            if (!this.users.length) {
                await this.$store.dispatch('users/load')
            }

            if (!this.subscriptions.length) {
                await this.$store.dispatch('vindiSubscriptions/load')
            }
        },
        async save() {
            const limit = this.payload.subscription_overrides.geo_seo_prompt_limit
            if (this.payload.subscription_overrides.geo_seo === true && (!Number.isSafeInteger(limit) || limit < 1)) {
                this.$toast('error', 'Informe um limite de prompts inteiro maior ou igual a 1.')
                return
            }
            const quantity = this.payload.subscription_overrides.articles_quantity
            if (this.payload.subscription_overrides.articles === true
                && (!Number.isInteger(quantity) || quantity < 1 || quantity > 9999)) {
                this.$toast('error', 'Informe uma quantidade mensal de artigos de 1 a 9.999.')
                return
            }
            this.saving = true
            const data = Object.fromEntries(Object.entries(JSON.parse(JSON.stringify(this.payload)))
                .filter(([key, value]) => JSON.stringify(value) !== JSON.stringify(this.originalPayload[key])))

            if (data.accesses) data.accesses = data.accesses.map(access => ({ user_id: access }))
            delete data.subscription_access
            delete data.articles_override_renewed_month
            delete data.role


            const response = await this.$api.updateUser({
                id: this.user._id,
                data
            })

            if (response.error) {
                this.saving = false
                return
            }

            const refreshed = await this.$api.getUserById({ id: this.user._id })
            if (!refreshed.error) this.user = refreshed.message
            setTimeout(() => {
                this.saving = false
                this.$toast('success', 'Usuário atualizado com sucesso!')
            }, 1000)
        },
    },
    watch: {
        value: {
            immediate: true,
            handler: 'load'
        }
    },

}
</script>

<style scoped>
.user-tab-panel__actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 12px;
}
</style>
